import { getHomeUrl, plainRichText } from '@obvio/app'

import { SITE_NAME } from '@/constants/seo'

const DESCRIPTION_MAX_LENGTH = 155

type PageBlock = {
  component?: string
  variant?: string
  data?: Record<string, unknown>
}

export type SiteLocale = 'pl' | 'en'

export function toSiteLocale(locale: string): SiteLocale {
  return locale === 'en' ? 'en' : 'pl'
}

/** Absolute URL of a path (given without locale prefix) in the given language. */
export function localizedUrl(path: string, locale: SiteLocale): string {
  const clean = path.replaceAll(/^\/+|\/+$/gu, '')
  const prefix = locale === 'en' ? '/en' : ''
  return `${getHomeUrl()}${prefix}${clean ? `/${clean}` : ''}`
}

export function withSiteName(title: string): string {
  return `${title} - ${SITE_NAME}`
}

export function truncate(text: string, max = DESCRIPTION_MAX_LENGTH): string {
  const normalized = text.replaceAll(/\s+/gu, ' ').trim()
  if (normalized.length <= max) {
    return normalized
  }
  const cut = normalized.slice(0, max - 1)
  const lastSpace = cut.lastIndexOf(' ')
  return `${lastSpace > 0 ? cut.slice(0, lastSpace) : cut}…`
}

/** Plain-text excerpt of a rich text value, suitable for a meta description. */
export function describeRichText(
  richText: string | null | undefined,
): string | undefined {
  if (!richText) {
    return undefined
  }
  const text = truncate(plainRichText(richText))
  return text || undefined
}

/**
 * Text of the first rich text found in the CMS blocks of a page. Used as a meta
 * description when the editor did not fill in `seoDescription`, so pages do not
 * all fall back to the (identical) site-wide description.
 */
export function describePage(
  blocks: PageBlock[] | undefined,
): string | undefined {
  for (const block of blocks ?? []) {
    const richText = block.data?.richText
    if (typeof richText === 'string') {
      const description = describeRichText(richText)
      if (description) {
        return description
      }
    }
  }
  return undefined
}

/**
 * Whether the hero blocks of a page render an `<h1>`. Mirrors `customRenderers`
 * in `Renderer/utils.tsx`; pages that do not get a hidden `<h1>` instead.
 */
export function pageHasHeading(blocks: PageBlock[] | undefined): boolean {
  return (blocks ?? []).some(({ component, variant, data }) => {
    const key = [component, variant].join('.')
    const seoTitle = typeof data?.seoTitle === 'string' ? data.seoTitle : ''
    const title = typeof data?.title === 'string' ? data.title : ''

    return (
      (key === 'hero.title-image' && Boolean(seoTitle || title)) ||
      (key === 'hero.youtube' && Boolean(seoTitle)) ||
      key === 'hero-kontakt.hero-map'
    )
  })
}
